import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import type { TeamPortal, TeamPortalMember } from '../models/team-portal'
import { httpStatusCode } from './http-status-code'

const compareMembers = (left: TeamPortalMember, right: TeamPortalMember) => {
  const byLastName = (left.lastName ?? '').localeCompare(right.lastName ?? '', 'de')
  if (byLastName !== 0) {
    return byLastName
  }
  return (left.firstName ?? '').localeCompare(right.firstName ?? '', 'de')
}

export const useTeamPortal = (
  teamId: MaybeRefOrGetter<string>,
  options?: { autoload?: boolean },
) => {
  const autoload = options?.autoload ?? true
  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const id = computed(() => toValue(teamId))
  const endpoint = computed(() => `portal/teams/${id.value}`)
  const linkEndpoint = computed(() => `${endpoint.value}/portal-link`)
  const memberCodeEndpoint = ref('portal/members/unknown/access-code')

  const { data, status, error, load } = useGet<TeamPortal>(endpoint)
  const {
    data: regeneratedLink,
    status: linkStatus,
    mutate: postLink,
  } = usePost<{ portalLinkToken: string }>(linkEndpoint)
  const {
    data: regeneratedCode,
    status: codeStatus,
    mutate: postCode,
  } = usePost<{ accessCode: string }>(memberCodeEndpoint)

  const tokenOverride = ref<string>()
  const codeOverrides = ref<Record<string, string>>({})
  const pendingMemberId = ref<string>()

  const portalLinkToken = computed(() => {
    if (tokenOverride.value !== undefined) {
      return tokenOverride.value
    }
    return data.value?.portalLinkToken ?? null
  })

  const portalUrl = computed(() => {
    const token = portalLinkToken.value
    if (!token) {
      return null
    }
    const resolved = router.resolve({
      name: 'portal',
      params: { portalLinkToken: token },
    })
    return `${window.location.origin}${resolved.fullPath}`
  })

  const members = computed(() => {
    const list = data.value?.members ?? []
    return [...list]
      .map((member) => ({
        ...member,
        accessCode: codeOverrides.value[member.memberId] ?? member.accessCode,
      }))
      .sort(compareMembers)
  })

  const teamName = computed(() => data.value?.teamName ?? '')

  const isNotFound = computed(
    () => status.value === ApiRequestStatus.Error && httpStatusCode(error.value) === 404,
  )

  watch(
    id,
    () => {
      if (!autoload) {
        return
      }
      tokenOverride.value = undefined
      codeOverrides.value = {}
      pendingMemberId.value = undefined
      load()
    },
    { immediate: true },
  )

  const saveLink = async (replacing: boolean) => {
    if (linkStatus.value === ApiRequestStatus.Loading) {
      return false
    }
    const requestedTeamId = id.value
    await postLink()
    if (id.value !== requestedTeamId) {
      return false
    }
    if (linkStatus.value === ApiRequestStatus.Success && regeneratedLink.value?.portalLinkToken) {
      tokenOverride.value = regeneratedLink.value.portalLinkToken
      snackStore.showInfo(
        t(replacing ? 'portal.link.regenerateSuccess' : 'portal.link.generateSuccess'),
      )
      return true
    }
    snackStore.showError(t('portal.link.saveError'))
    return false
  }

  const saveMemberCode = async (memberId: string) => {
    if (codeStatus.value === ApiRequestStatus.Loading) {
      return false
    }
    const member = members.value.find((item) => item.memberId === memberId)
    const replacing = member?.accessCode != null
    const requestedTeamId = id.value
    pendingMemberId.value = memberId
    memberCodeEndpoint.value = `portal/members/${memberId}/access-code`
    await postCode()
    pendingMemberId.value = undefined
    if (id.value !== requestedTeamId) {
      return false
    }
    if (codeStatus.value === ApiRequestStatus.Success && regeneratedCode.value?.accessCode) {
      codeOverrides.value = {
        ...codeOverrides.value,
        [memberId]: regeneratedCode.value.accessCode,
      }
      snackStore.showInfo(
        t(
          replacing
            ? 'organization.loginLessMember.accessCode.regenerateSuccess'
            : 'portal.accessCode.generateSuccess',
        ),
      )
      return true
    }
    snackStore.showError(t('organization.loginLessMember.accessCode.regenerateError'))
    return false
  }

  return {
    teamName,
    portalLinkToken,
    portalUrl,
    members,
    status,
    linkStatus,
    codeStatus,
    pendingMemberId,
    isNotFound,
    saveLink,
    saveMemberCode,
  }
}
